/**
 * @fileoverview `<Timeline>` — historique vertical d'événements métier.
 *
 * Le backoffice affiche plusieurs séquences de traitement : paiement,
 * enregistrement STI, remboursement, export ou audit. Ce composant impose un
 * rendu chronologique commun, sans ombre, avec des marqueurs sémantiques et
 * une API simple à consommer par les pages métier.
 */

import type { ReactNode } from 'react';
import { cn } from '../lib/utils.ts';

/**
 * Ton visuel appliqué au marqueur de l'événement.
 */
export type TimelineItemTone = 'neutral' | 'success' | 'info' | 'warning' | 'danger';

/**
 * Événement affiché dans `Timeline`.
 */
export interface TimelineItem {
  /** Identifiant stable de l'événement, utilisé comme clé React. */
  id: string;
  /** Titre court de l'événement. */
  title: ReactNode;
  /** Détail optionnel, une à deux lignes maximum. */
  description?: ReactNode;
  /** Date, heure ou libellé temporel déjà formaté par l'écran appelant. */
  timestamp?: ReactNode;
  /** Badge, statut ou action courte affiché à droite du titre. */
  meta?: ReactNode;
  /** Ton sémantique du marqueur. Défaut : `neutral`. */
  tone?: TimelineItemTone;
}

/**
 * Props publiques de `Timeline`.
 */
export interface TimelineProps {
  /** Liste ordonnée du plus ancien au plus récent. */
  items: readonly TimelineItem[];
  /** Label accessible de la liste. Défaut : `Historique`. */
  ariaLabel?: string;
  /** Réduit l'espacement vertical pour les panneaux denses. */
  compact?: boolean;
  /** Contenu affiché si `items` est vide. */
  emptyState?: ReactNode;
  /** Classe CSS complémentaire. */
  className?: string;
}

const MARKER_CLASSES: Record<TimelineItemTone, string> = {
  neutral: 'border-border bg-muted text-muted-foreground',
  success: 'border-status-paid/30 bg-status-paid text-white',
  info: 'border-quipux-blue-300 bg-quipux-blue-700 text-white',
  warning: 'border-warning/30 bg-warning text-white',
  danger: 'border-danger/30 bg-danger text-white',
};

/**
 * Historique vertical standard.
 *
 * @param props - Événements, label accessible, mode compact, empty state et classe CSS.
 * @returns Liste chronologique avec marqueurs sémantiques.
 *
 * @example
 * ```tsx
 * <Timeline
 *   items={[
 *     { id: 'created', title: 'Paiement créé', timestamp: '14 juil. 2026, 08:22' },
 *     { id: 'paid', title: 'Paiement confirmé', tone: 'success' },
 *   ]}
 * />
 * ```
 */
export function Timeline({
  items,
  ariaLabel = 'Historique',
  compact = false,
  emptyState,
  className,
}: TimelineProps): React.JSX.Element {
  if (items.length === 0) {
    return (
      <div className={cn('rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground', className)}>
        {emptyState ?? 'Aucun événement à afficher.'}
      </div>
    );
  }

  return (
    <ol className={cn('grid', className)} aria-label={ariaLabel}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const tone = item.tone ?? 'neutral';

        return (
          <li
            key={item.id}
            className={cn(
              'relative grid grid-cols-[1.25rem_minmax(0,1fr)] gap-3',
              compact ? 'pb-4' : 'pb-6',
              isLast && 'pb-0',
            )}
          >
            <div className="relative flex justify-center">
              {!isLast ? (
                <span
                  aria-hidden="true"
                  className="bg-border absolute top-5 bottom-0 w-px"
                />
              ) : null}
              <span
                aria-hidden="true"
                className={cn(
                  'relative z-10 mt-0.5 flex size-4 items-center justify-center rounded-full border-2',
                  MARKER_CLASSES[tone],
                )}
              >
                <span className="size-1.5 rounded-full bg-current" />
              </span>
            </div>

            <div className="min-w-0">
              <div className="flex items-start justify-between gap-3">
                <p className="text-quipux-navy-950 text-sm font-semibold leading-5">
                  {item.title}
                </p>
                {item.meta ? <div className="shrink-0">{item.meta}</div> : null}
              </div>
              {item.timestamp ? (
                <p className="text-muted-foreground mt-1 text-xs leading-5">{item.timestamp}</p>
              ) : null}
              {item.description ? (
                <p className="text-muted-foreground mt-1 text-sm leading-5">{item.description}</p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
